import { useState, useMemo } from "react";
import { useDates } from "../../../dates/DatesContext";
import { useFetch } from "../../../hooks/api";
import { ProductsTable } from "../../../components/core/blocks/ProductsTable";
import { ProductCard } from "../ProductCard";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { SetValue, sendUpdates } from "../components/SetValue";
import Loading from "../../../components/core/blocks/Loading";
import RemoveProductsFromPlanModal from "../components/RemoveProducts";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useUpdateStockRequired } from "../api/useUpdateStockRequired";
import { Row, Col, Nav } from "react-bootstrap";
import IncomingInventory from "../../../components/custom/IncomingInventory";
import ProductInventory from "../../../components/custom/ProductInventory";
import TrellisNavLink from "../../../components/core/basic/NavLink";
import { useModal } from "@ebay/nice-modal-react";
import { SetProductPriceOffsetModal } from "../components/modals/SetOffset";
import { useAppSelector } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";

const ExpandedContent = ({ marketPlace, row }) => {
  const { t } = useTranslation(["pricingv2"]);
  return (
    <div style={{ backgroundColor: "#efefef" }}>
      <ProductCard
        marketPlace={marketPlace}
        productId={row?.original?.product_id}
        additionalBadges={[
          row.original.stock_required && {
            text: t(($) => $.pricingv2["Stock Required"]),
          },
        ].filter(Boolean)}
      />
    </div>
  );
};

export const ProductsTab = ({ marketPlace, pricingPlan }) => {
  const { t } = useTranslation(["pricingv2"]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [valueType, setValueType] = useState("min_price");
  const [showSetValue, setShowSetValue] = useState(false);
  const removeProductsFromPlanModal = useModal(RemoveProductsFromPlanModal);
  const [tab, setTab] = useState("products");
  const queryClient = useQueryClient();
  const hasBuyBoxModule = useModuleEnabled("BUY_BOX");
  const channel = useAppSelector((state) => state.channel)?.currentChannel;

  const updateProducts = useMutation({
    mutationFn: async (updates) => {
      await sendUpdates("product_id", updates, marketPlace, queryClient);
    },
  });

  const updateStockRequiredMutation = useUpdateStockRequired();
  const setPriceOffsetModal = useModal(SetProductPriceOffsetModal);

  const { group, start, end, preStart, preEnd } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "product_id",
    plan_id: pricingPlan.id,
    include: "pricing",
    channel: channel,
  };

  const productReportQuery = useFetch([], "/api/data_report/product_report", filters, {
    select: (d) => d.data,
    placeholderData: keepPreviousData,
  });

  const productData = useMemo(() => {
    if (productReportQuery.data?.products) {
      return productReportQuery.data.products.map((product) => {
        if (!product.tags) product.tags = [];
        if (product.stock_required) product.tags.push(t(($) => $.pricingv2["Stock Required"]));
        return {
          ...product,
        };
      });
    }
    return [];
  }, [productReportQuery.data, t]);

  const { productById, totalUnits } = useMemo(() => {
    const ids = {};
    let totalUnits = 0;
    if (productReportQuery.data?.products) {
      for (const product of productReportQuery.data.products) {
        ids[product.product_id] = product;
        totalUnits += product.total_units;
      }
    }
    return { productById: ids, totalUnits };
  }, [productReportQuery.data]);

  return (
    <div>
      {productReportQuery.isLoading && (
        <div className="py-5">
          <Loading fullPage={false} />
        </div>
      )}
      {productReportQuery.isError && <p>{t(($) => $.pricingv2["Error Loading Dashboard"])}</p>}
      {productReportQuery.isSuccess && (
        <>
          <Row>
            <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
              <div className="pt-2"></div>
              <Nav
                variant="underline"
                onSelect={(k) => {
                  return setTab(k);
                }}
                activeKey={tab}
                className="flex-column text-start"
              >
                <TrellisNavLink eventKey="products" className="text-start">
                  {t(($) => $.pricingv2["Products"])}
                </TrellisNavLink>

                <TrellisNavLink eventKey="historical-inventory" className="text-start">
                  {t(($) => $.pricingv2["Historical Inventory"])}
                </TrellisNavLink>
                <TrellisNavLink
                  eventKey="incoming-inventory"
                  className="text-start"
                  visible={pricingPlan?.pricing_org?.use_incoming_inventory}
                >
                  {t(($) => $.pricingv2["Incoming Inventory"])}
                </TrellisNavLink>
              </Nav>
            </Col>
            <Col xs={10}>
              {tab === "products" && (
                <ProductsTable
                  onBulkChange={async (changes, clearChanges) => {
                    const updates = {};
                    Object.entries(changes).forEach(([id, changes]) => {
                      updates[id] = Object.entries(changes)
                        .filter(([key]) => ["max_price", "min_price", "shipping_cost", "landed_cost"].includes(key))
                        .map(([key, value]) => [key, parseFloat(value)]);
                    });
                    updateProducts.mutate(updates, {
                      onSuccess: () => {
                        clearChanges();
                      },
                    });
                  }}
                  renderProductStatusToggles={hasBuyBoxModule}
                  marketPlace={marketPlace}
                  productData={productData}
                  ExpandedContent={ExpandedContent}
                  isLoading={
                    updateProducts.isPending || updateStockRequiredMutation.isLoading || productReportQuery.isFetching
                  }
                  selectedProducts={selectedProducts}
                  onSelection={setSelectedProducts}
                  bulkActionOptions={[
                    {
                      label: t(($) => $.pricingv2["Remove Products from Pricing Plan"]),
                      action: (selected) => {
                        removeProductsFromPlanModal.show({
                          selectedProducts: selected,
                          productById,
                          onRemove: () => setSelectedProducts([]),
                        });
                      },
                    },
                    {
                      divider: true,
                    },
                    {
                      label: t(($) => $.pricingv2["Set Fixed Price"]),
                      action: () => {
                        setValueType("Fixed Price");
                        setShowSetValue(true);
                      },
                    },
                    {
                      label: t(($) => $.pricingv2["Clear Fixed Price"]),
                      action: () => {
                        setValueType("Clear Fixed Price");
                        setShowSetValue(true);
                      },
                    },
                    {
                      label: t(($) => $.pricingv2["Set Shipping Cost"]),
                      action: () => {
                        setValueType("Shipping Cost");
                        setShowSetValue(true);
                      },
                    },
                    {
                      label: t(($) => $.pricingv2["Set Landed Cost"]),
                      action: () => {
                        setValueType("Landed Cost");
                        setShowSetValue(true);
                      },
                    },
                    {
                      label: t(($) => $.pricingv2["Set Price Offset"]),
                      action: (selected) => {
                        setPriceOffsetModal.show({
                          planId: pricingPlan.id,
                          productData: productData.filter((p) => selected.includes(p.product_id)),
                        });
                      },
                    },
                    {
                      label: t(($) => $.pricingv2["Set Stock Required"]),
                      action: (selected) => {
                        updateStockRequiredMutation.mutate({
                          plan_id: pricingPlan.id,
                          product_ids: selected,
                          stock_required: true,
                        });
                      },
                    },
                    {
                      label: t(($) => $.pricingv2["Clear Stock Required"]),
                      action: async (selected) => {
                        updateStockRequiredMutation.mutate({
                          plan_id: pricingPlan.id,
                          product_ids: selected,
                          stock_required: false,
                        });
                      },
                    },
                  ]}
                />
              )}
              {tab === "historical-inventory" && (
                <Row>
                  <Col xs={12}>
                    <ProductInventory
                      productIds={Object.keys(productById)}
                      start={start}
                      end={end}
                      productPrice={pricingPlan.best_price}
                      marketPlace={marketPlace.marketPlace}
                      totalUnits={totalUnits}
                    />
                  </Col>
                </Row>
              )}

              {tab === "incoming-inventory" && <IncomingInventory product={productData[0]} />}
            </Col>
          </Row>
          <SetValue
            showSetValue={showSetValue}
            setShowSetValue={setShowSetValue}
            valueType={valueType}
            selectedProducts={selectedProducts}
            productById={productById}
            marketPlace={marketPlace}
            setSelectedProducts={setSelectedProducts}
          />
        </>
      )}
    </div>
  );
};
